import {isEqual} from 'lodash';
import {ProtocolFormData} from '../../../../../../redux/slice/aiReviewSliceV2';

const isBlankValue = (value: unknown): boolean => {
  if (value == null) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  if (Array.isArray(value)) {
    return value.every(isBlankValue);
  }
  if (typeof value === 'object') {
    return Object.values(value).every(isBlankValue);
  }
  return false;
};

const compactValue = (value: unknown): unknown => {
  if (value == null) {
    return undefined;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  }
  if (Array.isArray(value)) {
    return value.map(compactValue).filter((item) => !isBlankValue(item));
  }
  if (typeof value === 'object') {
    // Keys come from known protocol section objects, not user-controlled indexes.
    // eslint-disable-next-line security/detect-object-injection
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .map(([key, entry]) => [key, compactValue(entry)] as const)
        .filter(([, compacted]) => !isBlankValue(compacted))
    );
  }
  return value;
};

const compactProtocolSections = (protocol: ProtocolFormData) => ({
  caseContext: compactValue(protocol.caseContext) ?? {},
  relevanceCriteria: compactValue(protocol.relevanceCriteria) ?? {},
  issueCoding: compactValue(protocol.issueCoding) ?? {},
});

/** True when any Case Context / Relevance Criteria / Issue Coding field has content. */
export const hasMeaningfulProtocolContent = (
  protocol: ProtocolFormData
): boolean => {
  const sections = compactProtocolSections(protocol);
  return (
    !isBlankValue(sections.caseContext) ||
    !isBlankValue(sections.relevanceCriteria) ||
    !isBlankValue(sections.issueCoding)
  );
};

export const isProtocolContentEdited = (
  current: ProtocolFormData,
  baseline: ProtocolFormData
): boolean =>
  !isEqual(compactProtocolSections(current), compactProtocolSections(baseline));
